import { useSyncExternalStore } from 'react';
import { setBrandName } from '../i18n/index.ts';
import { dark as darkTheme, light as lightTheme } from '../theme/tokens.ts';

/**
 * White-label: a partner's brand in place of ERP PRO's (server: partners.ts). It comes from the
 * address the browser opened (before sign-in) and from the business once signed in. It sets the
 * name the app says, the logo, the main colour, the tab's title and icon.
 */

export interface Brand {
  partner: string;
  name: string;
  accent: string | null;
  logo: string | null;
  support_email: string | null;
  support_phone: string | null;
  website: string | null;
  powered_by: boolean;
}

let current: Brand | null = null;
/** The brand of the address itself, which signing out returns to. */
let ofHost: Brand | null = null;
const listeners = new Set<() => void>();

const hex = (h: string) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16)).join(' ');

function colours(b: Brand | null): string {
  const a = b?.accent;
  if (!a || !/^#[0-9a-f]{6}$/i.test(a)) return '';
  const light = `--primary: ${a};
  --primary-hover: color-mix(in srgb, ${a} 82%, black);
  --primary-ink: ${lightTheme['primary-ink']};
  --primary-soft: color-mix(in srgb, ${a} 9%, ${lightTheme.surface});
  --primary-soft-ink: color-mix(in srgb, ${a} 78%, black);
  --primary-rgb: ${hex(a)};`;
  const dark = `--primary: color-mix(in srgb, ${a} 55%, white);
  --primary-hover: color-mix(in srgb, ${a} 40%, white);
  --primary-ink: ${darkTheme['primary-ink']};
  --primary-soft: color-mix(in srgb, ${a} 30%, ${darkTheme.surface});
  --primary-soft-ink: color-mix(in srgb, ${a} 25%, white);`;
  return `:root { ${light} }
@media (prefers-color-scheme: dark) { :root:not([data-theme='light']) { ${dark} } }
:root[data-theme='dark'] { ${dark} }`;
}

function apply(b: Brand | null) {
  setBrandName(b?.name ?? null);
  let style = document.getElementById('brand-tokens');
  if (!style) {
    style = document.createElement('style');
    style.id = 'brand-tokens';
    document.head.appendChild(style);
  }
  style.textContent = colours(b);
  document.documentElement.classList.toggle('branded', !!b?.logo);
  document.title = b?.name ?? 'ERP PRO';
  for (const link of document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]')) {
    if (!link.dataset.erp) link.dataset.erp = link.href;
    link.href = b?.logo ?? link.dataset.erp;
  }
}

export function setBrand(b: Brand | null) {
  const next = b ?? ofHost;
  if (JSON.stringify(next) === JSON.stringify(current)) return;
  current = next;
  apply(current);
  for (const l of listeners) l();
}

/** Asks the server which brand this address carries; waits at most a moment. */
export async function loadHostBrand(): Promise<void> {
  try {
    const ctl = new AbortController();
    const timer = window.setTimeout(() => ctl.abort(), 1500);
    const res = await fetch('/api/brand', { signal: ctl.signal, credentials: 'omit' });
    window.clearTimeout(timer);
    if (!res.ok) return;
    ofHost = ((await res.json()) as { brand: Brand | null }).brand;
    setBrand(ofHost);
  } catch {
    // ERP PRO's own look.
  }
}

export const brandNow = () => current;

export function useBrand(): Brand | null {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => current,
  );
}
